import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CCY_CODES,
  D,
  type Account,
  type CcyCode,
  type CreateAccountInput,
  type Money,
  type UpdateAccountInput,
} from '@wave/shared';
import { Modal } from '../Modal';
import { Combobox } from '../Combobox';
import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  fieldLabelCls,
  inputCls,
  textareaCls,
} from '../../lib/styles';
import { useCreateChat, useDeleteChat, useUpdateChat } from '../../api/chats';
import { useCurrencies } from '../../api/hooks';
import { hasRight, useAuthStore } from '../../auth/store';

interface Props {
  mode: 'create' | 'edit';
  initial?: Account;
  onClose: () => void;
  onDone: () => void;
}

const KIND_OPTIONS = [
  { value: 'client', label: 'Клиент' },
  { value: 'executor', label: 'Исполнитель' },
];

export function ChatEditModal({ mode, initial, onClose, onDone }: Props) {
  const navigate = useNavigate();
  const create = useCreateChat();
  const update = useUpdateChat();
  const del = useDeleteChat();
  // Удаление контрагента остаётся под refs.write (админ) — оператор/менеджер
  // (accounts.manage) могут только создавать/редактировать.
  const canDelete = hasRight(useAuthStore((s) => s.rights), 'refs.write');
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [code, setCode] = useState(initial?.code ?? '');
  const [kind, setKind] = useState<'client' | 'executor'>(initial?.kind ?? 'client');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [telegramChatId, setTelegramChatId] = useState(initial?.telegramChatId ?? '');
  const [telegramChatTitle, setTelegramChatTitle] = useState(initial?.telegramChatTitle ?? '');
  const [invertBalance, setInvertBalance] = useState(initial?.invertBalance ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [balances, setBalances] = useState<Record<string, string>>(
    () => (initial?.initialBalances as Record<string, string>) ?? {},
  );

  useEffect(() => {
    if (initial) {
      setCode(initial.code);
      setKind(initial.kind);
      setDisplayName(initial.displayName ?? '');
      setIsActive(initial.isActive);
      setTelegramChatId(initial.telegramChatId ?? '');
      setTelegramChatTitle(initial.telegramChatTitle ?? '');
      setInvertBalance(initial.invertBalance);
      setNotes(initial.notes ?? '');
      setBalances((initial.initialBalances as Record<string, string>) ?? {});
    }
  }, [initial]);

  function close() {
    onClose();
    if (mode === 'create') navigate(-1);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!code.trim()) {
      setError('Код чата обязателен');
      return;
    }
    if (mode === 'create' && code.trim().length > 64) {
      setError('Код не должен быть длиннее 64 символов');
      return;
    }
    if (telegramChatId && !/^-?\d+$/.test(telegramChatId.trim())) {
      setError('Telegram chat_id должен быть числом');
      return;
    }
    // очищаем balances от пустых/нулевых
    const cleanBalances: Record<string, string> = {};
    for (const [ccy, val] of Object.entries(balances)) {
      const v = (val ?? '').trim();
      if (!v) continue;
      try {
        const d = D(v);
        if (d.isZero()) continue;
        cleanBalances[ccy] = d.toString();
      } catch {
        setError(`Невалидное число в ${ccy}: ${v}`);
        return;
      }
    }

    try {
      if (mode === 'create') {
        const payload: CreateAccountInput = {
          code: code.trim(),
          kind,
          displayName: displayName.trim() || null,
          isActive,
          initialBalances: cleanBalances,
          telegramChatId: telegramChatId.trim() || null,
          telegramChatTitle: telegramChatTitle.trim() || null,
          invertBalance,
          notes: notes.trim() || null,
        };
        await create.mutateAsync(payload);
      } else if (initial) {
        const patch: UpdateAccountInput = {
          kind,
          displayName: displayName.trim() || null,
          isActive,
          initialBalances: cleanBalances,
          telegramChatId: telegramChatId.trim() || null,
          telegramChatTitle: telegramChatTitle.trim() || null,
          invertBalance,
          notes: notes.trim() || null,
        };
        await update.mutateAsync({ code: initial.code, patch });
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function onDelete() {
    if (!initial) return;
    setError(null);
    try {
      const res = await del.mutateAsync(initial.code);
      onDone();
      // Можно показать toast «удалён» или «деактивирован» — пока тихо
      void res;
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const isPending = create.isPending || update.isPending || del.isPending;
  const title = mode === 'create' ? 'Новый чат' : `Чат · ${initial?.code ?? ''}`;

  return (
    <Modal title={title} onClose={close} size="md">
      <form onSubmit={submit} className="space-y-5">
        {/* Основные */}
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text2">
            Идентификация
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Код">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={mode === 'edit'}
                placeholder="KSAN-0143"
                className={`${inputCls} font-mono`}
                required
              />
              {mode === 'edit' && (
                <div className="mt-1 text-[11px] text-text-subtle">Код не редактируется после создания.</div>
              )}
            </Field>
            <Field label="Роль">
              <Combobox
                value={kind}
                onChange={(val) => setKind(val as 'client' | 'executor')}
                options={KIND_OPTIONS}
                searchable={false}
              />
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Отображаемое имя">
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Касандра / Иван / …"
                className={inputCls}
              />
            </Field>
            <div className="flex items-end">
              <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-border bg-bg px-3 text-sm transition-colors duration-150 hover:bg-bg2">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-3.5 w-3.5 accent-accent"
                />
                <span className="text-text">Активен</span>
                <span className="text-xs text-text2">
                  {isActive ? '· виден в формах' : '· скрыт'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Telegram */}
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text2">
            Telegram-привязка
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_2fr]">
            <Field label="chat_id">
              <input
                value={telegramChatId}
                onChange={(e) => setTelegramChatId(e.target.value)}
                placeholder="-1001234567890"
                className={`${inputCls} font-mono`}
              />
            </Field>
            <Field label="Название чата">
              <input
                value={telegramChatTitle}
                onChange={(e) => setTelegramChatTitle(e.target.value)}
                placeholder="KSAN trading"
                className={inputCls}
              />
            </Field>
          </div>
          <div className="mt-1 text-[11px] text-text-subtle">
            chat_id для Telegram-ботов. У супергрупп — отрицательное число вида <code className="font-mono">-100…</code>.
          </div>
          <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-bg px-3 py-2 text-sm transition-colors duration-150 hover:bg-bg2">
            <input
              type="checkbox"
              checked={invertBalance}
              onChange={(e) => setInvertBalance(e.target.checked)}
              className="h-3.5 w-3.5 accent-accent"
            />
            <span className="text-text">Инвертированные знаки остатков</span>
          </label>
          <div className="mt-1 text-[11px] text-text-subtle">
            Обычная модель: плюс — мы должны клиенту, минус — клиент должен нам. Включите, если
            в этом чате знаки наоборот — на странице балансов они будут приведены к общей модели.
          </div>
        </div>

        {/* Initial balances */}
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text2">
            Стартовые балансы
          </h3>
          <BalancesEditor balances={balances} onChange={setBalances} />
          <p className="mt-1 text-xs text-text2">
            Долг чата на момент подключения к системе. Участвует в аудите балансов
            (план = стартовый баланс + открытые проводки). Знак: плюс = «мы должны»,
            минус = «нам должны».
          </p>
        </div>

        {/* Notes */}
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text2">
            Примечания
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Любая внутренняя информация о контрагенте…"
            className={textareaCls}
          />
        </div>

        {error && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle pt-4">
          <div>
            {mode === 'edit' && initial && canDelete && (
              confirmDelete ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-text2">Удалить?</span>
                  <button type="button" onClick={onDelete} disabled={isPending} className={btnDanger}>
                    Подтвердить
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(false)} className={btnSecondary}>
                    Отмена
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setConfirmDelete(true)} className={btnDanger}>
                  Удалить
                </button>
              )
            )}
          </div>
          {!confirmDelete && (
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={close} className={btnSecondary}>
                Отмена
              </button>
              <button type="submit" disabled={isPending} className={btnPrimary}>
                {isPending ? 'Сохранение…' : mode === 'create' ? 'Создать' : 'Сохранить'}
              </button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

function BalancesEditor({
  balances,
  onChange,
}: {
  balances: Record<string, Money>;
  onChange: (next: Record<string, Money>) => void;
}) {
  const [pickedCcy, setPickedCcy] = useState<CcyCode | ''>('');
  // Валюты из справочника (вкл. добавленные через UI); fallback — встроенные.
  const { data: currenciesList } = useCurrencies();
  const allCodes: readonly CcyCode[] = currenciesList?.length
    ? currenciesList.map((c) => c.code)
    : CCY_CODES;

  const present = allCodes.filter((c) => balances[c] !== undefined);
  const available = allCodes.filter((c) => balances[c] === undefined);

  function setVal(ccy: CcyCode, value: string) {
    onChange({ ...balances, [ccy]: value });
  }
  function addCcy() {
    if (!pickedCcy) return;
    onChange({ ...balances, [pickedCcy]: '' });
    setPickedCcy('');
  }
  function remove(ccy: CcyCode) {
    const next = { ...balances };
    delete next[ccy];
    onChange(next);
  }

  return (
    <div className="space-y-2">
      {present.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-bg/50 px-3 py-2 text-xs text-text2">
          Балансов нет. Добавьте валюту ниже, если нужно проставить стартовый остаток.
        </div>
      )}
      {present.map((ccy) => (
        <div key={ccy} className="grid grid-cols-[80px_1fr_auto] items-center gap-2">
          <span className="font-mono text-sm font-semibold text-text">{ccy}</span>
          <input
            value={balances[ccy] ?? ''}
            onChange={(e) => setVal(ccy as CcyCode, e.target.value)}
            placeholder="0"
            className={`${inputCls} font-mono tabular-nums text-right`}
          />
          <button
            type="button"
            onClick={() => remove(ccy as CcyCode)}
            aria-label="Убрать валюту"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-bg text-text2 transition-colors duration-150 hover:bg-[var(--red-bg)] hover:text-danger"
          >
            ×
          </button>
        </div>
      ))}
      {available.length > 0 && (
        <div className="flex items-end gap-2 pt-1">
          <div className="w-32">
            <Combobox
              value={pickedCcy}
              onChange={(val) => setPickedCcy(val as CcyCode)}
              options={available.map((c) => ({ value: c, label: c }))}
              searchable={false}
              placeholder="+ валюта"
            />
          </div>
          <button
            type="button"
            disabled={!pickedCcy}
            onClick={addCcy}
            className={btnSecondary}
          >
            Добавить
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className={fieldLabelCls}>{label}</span>
      {children}
    </label>
  );
}
